/**
 * Turning a stream of ticks into a chart that moves.
 *
 * ## What was missing
 *
 * Both surfaces received live prices and neither drew them. The web opened the
 * feed and tracked `lastPrice` as a number beside the chart, applying only bars
 * the server marked `complete`; mobile never opened the feed at all. So the
 * newest candle sat frozen at whatever it looked like when the page loaded,
 * however far price had travelled since.
 *
 * The forming candle is the one a trader is actually watching. Everything here
 * exists to make it move.
 *
 * ## Why the merge is pure
 *
 * A tick arriving at a price is a small piece of arithmetic on the last bar —
 * extend the high, extend the low, move the close — and occasionally the
 * decision that this tick belongs to a bar that does not exist yet. That is
 * exactly the kind of logic that is miserable to debug inside a component and
 * trivial to prove out here.
 */
import type { Bar } from './types.js';

/**
 * Seconds per timeframe.
 *
 * Duplicated from `@aegis/core`'s backtest source rather than imported: this
 * package is the client's, and pulling the backtest engine into a phone bundle
 * to read nine numbers is the wrong trade. `live.test.ts` asserts the two
 * agree, so the duplication cannot drift silently.
 */
export const TIMEFRAME_SECONDS: Readonly<Record<string, number>> = {
  '1m': 60, '3m': 180, '5m': 300, '15m': 900, '30m': 1800,
  '1h': 3600, '4h': 14_400, '1d': 86_400, '1w': 604_800,
};

/** The start of the bar a timestamp falls in. */
export function barOpenTime(atSec: number, stepSec: number): number {
  return Math.floor(atSec / stepSec) * stepSec;
}

/**
 * Fold a tick into a series.
 *
 * Three cases, and the third is the one that matters:
 *
 * 1. **Same bar.** High and low stretch to admit the price; close becomes it.
 *    Open never moves — a bar's open is decided once, by its first trade.
 * 2. **A later bar.** The tick opens a new candle at that price, flat: open,
 *    high, low and close all equal until something else arrives.
 * 3. **An earlier bar.** Dropped. Out-of-order ticks happen on reconnect, when
 *    a buffered message lands after a newer one, and rewriting a closed bar
 *    from a stale tick would silently corrupt history that is already drawn.
 *
 * Returns the same array reference when nothing changed, so a caller can use
 * identity to skip a render.
 */
export function applyTick(
  bars: readonly Bar[],
  price: number,
  atSec: number,
  timeframe: string,
): readonly Bar[] {
  const step = TIMEFRAME_SECONDS[timeframe];
  if (step === undefined || !Number.isFinite(price) || !Number.isFinite(atSec)) return bars;

  const openTime = barOpenTime(atSec, step);
  const last = bars[bars.length - 1];

  // Nothing to extend. A tick before any history is not enough to draw from —
  // it would put a lone candle on an empty chart at an arbitrary time.
  if (!last) return bars;

  if (openTime < last.time) return bars;

  if (openTime === last.time) {
    if (price === last.close && price <= last.high && price >= last.low) return bars;
    const next = bars.slice();
    next[next.length - 1] = {
      ...last,
      high: Math.max(last.high, price),
      low: Math.min(last.low, price),
      close: price,
    };
    return next;
  }

  return [...bars, {
    time: openTime,
    open: price,
    high: price,
    low: price,
    close: price,
    volume: 0,
  }];
}

/**
 * Replace or append a bar the server has published.
 *
 * The server is authoritative: its bar carries real volume and a close agreed
 * with the venue, where a locally merged one carries whatever ticks happened to
 * arrive. So a bar for a time already held overwrites it rather than merging.
 */
export function applyBar(bars: readonly Bar[], bar: Bar): readonly Bar[] {
  const last = bars[bars.length - 1];
  if (last && bar.time === last.time) {
    const next = bars.slice();
    next[next.length - 1] = bar;
    return next;
  }
  if (last && bar.time < last.time) return bars;
  return [...bars, bar];
}

/** Seconds until the bar opened at `openTime` closes. Never negative. */
export function secondsToClose(openTime: number, timeframe: string, nowSec: number): number {
  const step = TIMEFRAME_SECONDS[timeframe];
  if (step === undefined) return 0;
  return Math.max(0, openTime + step - Math.floor(nowSec));
}

/**
 * `152` -> `02:32`, `3725` -> `1:02:05`.
 *
 * Minutes are padded and hours are not, so the common case reads as a stopwatch
 * and a daily bar's countdown does not pretend to be one.
 */
export function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}

/**
 * Which way to colour the last-price marker.
 *
 * By the forming candle's own direction — close against open — which is what
 * the candle body already shows. Colouring by the previous TICK instead would
 * make the label flicker red and green while the candle stayed green, and the
 * two are meant to agree.
 *
 * A doji reports 'flat' rather than picking a side.
 */
export function priceDirection(bar: Bar | undefined): 'up' | 'down' | 'flat' {
  if (!bar) return 'flat';
  if (bar.close > bar.open) return 'up';
  if (bar.close < bar.open) return 'down';
  return 'flat';
}

// ---------------------------------------------------------------------------
// Net change
// ---------------------------------------------------------------------------

export interface NetChange {
  /** In the instrument's own price units. */
  readonly absolute: number;
  /** Percent of the reference, already multiplied by 100. */
  readonly percent: number;
  /** What the change is measured FROM, so the UI can say. */
  readonly basis: 'prior_close' | 'open';
}

/**
 * How far price has moved this session.
 *
 * ## Why this takes DAILY bars rather than a session rule
 *
 * A header saying "+0.00021 (+0.02%)" has to answer "since when", and every
 * honest answer to that is a trading-style decision: RTH or ETH, which
 * timezone, whether a Sunday-evening open starts Monday. CLAUDE.md's "no
 * default trading style" makes those user preferences with neutral defaults,
 * and nothing on the client is entitled to pick one.
 *
 * So this does not decide. It takes the server's own daily bars — the same
 * `1d` series the timeframe control already offers — and reads the boundary
 * off them. Whatever session definition produced those bars is the one in
 * force, and it lives on the server where it belongs.
 *
 * ## The reference is the PRIOR close, not today's open
 *
 * Which is what a quote screen means by net change, and what a futures trader
 * reads as change from settlement. Measuring from today's open would report
 * zero at the open of a session that gapped, which is exactly the session
 * where the number matters.
 *
 * The last daily bar is always the current-or-most-recent session, so the one
 * before it is always the prior close — that holds whether the market is open
 * or shut, which is why there is no "is it open" branch here.
 *
 * With only one daily bar there is no prior session to measure from, so it
 * falls back to that bar's open and says so through `basis`. A caller that
 * needs to distinguish them can; one that does not gets a number either way.
 *
 * Returns null when there is nothing to measure at all, rather than a zero —
 * a zero would render as "unchanged", which is a claim, and no data is not.
 *
 * Known edge: a tick can arrive for a session whose daily bar the server has
 * not opened yet, which measures from one session too far back until the
 * daily series catches up. Refetched per symbol, so it resolves itself.
 */
export function netChange(daily: readonly Bar[], last: number | null): NetChange | null {
  const newest = daily[daily.length - 1];
  if (!newest) return null;

  const current = last !== null && Number.isFinite(last) ? last : newest.close;
  const prior = daily[daily.length - 2];
  const reference = prior ? prior.close : newest.open;
  const basis = prior ? 'prior_close' : 'open';

  if (!Number.isFinite(current) || !Number.isFinite(reference)) return null;

  const absolute = current - reference;
  // A reference of zero has no percentage. Nothing tradeable prices at zero,
  // but a bad bar should not put NaN in a header.
  const percent = reference === 0 ? 0 : (absolute / reference) * 100;
  return { absolute, percent, basis };
}

/**
 * Net change as it is printed, at the instrument's own precision.
 *
 * Signed explicitly on both figures, including the percent: a change shown as
 * "0.00021 (0.02%)" reads as a magnitude, and the direction is the half of it
 * a trader is looking for.
 */
export function formatNetChange(change: NetChange | null, precision: number): string {
  if (!change) return '';
  const dp = Math.max(0, Math.min(10, Math.trunc(precision)));
  const sign = (n: number): string => (n > 0 ? '+' : n < 0 ? '-' : '');
  const abs = `${sign(change.absolute)}${Math.abs(change.absolute).toFixed(dp)}`;
  const pct = `${sign(change.percent)}${Math.abs(change.percent).toFixed(2)}%`;
  return `${abs} (${pct})`;
}
